import * as React from 'react';
import { useLoadScript } from '@react-google-maps/api';

// The same libraries as every other screen's loader: Google's loader refuses
// a second call with different options.
const GOOGLE_MAPS_LIBS: 'places'[] = ['places'];

declare global {
  interface Window {
    /** Google calls this when it refuses the key; see refusals below. */
    gm_authFailure?: () => void;
  }
}

/*
 * Google refuses a key it will not serve, a referrer it was not set up for
 * (RefererNotAllowedMapError) or billing switched off, after the script has
 * loaded: not as a load error but by calling window.gm_authFailure, and
 * then drawing a grey "This page can't load Google Maps correctly" box where
 * the map was. So the refusal is caught here, once for the whole app, and
 * every screen that asks after it hears.
 */
let refused = false;
const listeners = new Set<() => void>();
if (typeof window !== 'undefined') {
  window.gm_authFailure = () => {
    refused = true;
    listeners.forEach((listener) => listener());
  };
}
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const isRefused = () => refused;

/**
 * The app's Google map script, loaded once for every screen that draws one.
 *
 * `mapsAvailable` is whether a map can be drawn at all: there is a key, the
 * script loaded, and Google has not refused the key. A screen with a map
 * uses it to leave the map out rather than show a grey box: the call screen
 * becomes a plain page and the calls tab drops its map card. It is true
 * while the script is still loading, so a working map is not held back; a
 * refusal arrives after the load and takes the map away then.
 */
export const useGoogleMaps = () => {
  const key = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: key,
    libraries: GOOGLE_MAPS_LIBS,
  });
  const keyRefused = React.useSyncExternalStore(
    subscribe,
    isRefused,
    isRefused
  );
  return {
    isLoaded,
    loadError,
    mapsAvailable: !!key && !loadError && !keyRefused,
  };
};
